import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {Box, Button, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useRegistryProject} from "../RegistryProjectContext";
import {CampaignRepository} from "../../../repositories/CampaignRepository";
import useConfirm from "../../../hooks/useConfirm";
import {useCollectionView} from "../../../hooks/useCollectionView";
import {CollectionViewToggle} from "../../../components/CollectionViewToggle";
import {CampaignsTableNew} from "./components/CampaignsTableNew";
import {CampaignsCalendar} from "./components/campaign-calendar/CampaignsCalendar";
import {CampaignFormDialogNew} from "./components/campaign-form/CampaignFormDialogNew";
import {useAmiExtensions} from "../../project/campaigns/hooks/useAmiExtensions";
import {CampaignDetailsNew} from "./CampaignDetailsNew";
import {ConversationHrefContext} from "../../project/campaigns/components/campaign-details/ConversationLink";
import {PillSelect} from "../dashboard/components/Panel";
import {subProjectLabel} from "../../../utils/subProjects";

// Outbound campaigns of the registry project: all of them, or one language's (filter). A campaign belongs to the
// project and one of its sub projects. The open campaign is kept in the URL (?campaign=12).
export const RegistryCampaignsPage = () => {
  const {project, subProject, basePath} = useRegistryProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = searchParams.get("campaign");

  const [storedView, setViewMode] = useCollectionView("registry-campaigns-view", "calendar");
  const viewMode = storedView === "cards" ? "cards" : "calendar";
  // "" = every language
  const [subProjectId, setSubProjectId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createRange, setCreateRange] = useState(null);
  const confirmDelete = useConfirm("Delete campaign?", "The campaign and all its phone numbers will be deleted.", "Delete");

  const subProjects = project.subProjects;
  // every sub project's extensions in one call
  const {getExtension} = useAmiExtensions(project.id);

  const languageName = useCallback((campaign) => {
    const language = subProjects.find(item => String(item.id) === String(campaign?.subProjectId));
    return language ? language.languageCode?.toUpperCase() || language.languageName : null;
  }, [subProjects]);

  const fetchCampaigns = useCallback(() => {
    setLoading(true);
    CampaignRepository.getAll({projectId: String(project.id), subProjectId: subProjectId || undefined})
      .then(response => setCampaigns(response?.data || []))
      .catch(error => {
        console.error(error);
        toast.error("Failed to load campaigns");
      })
      .finally(() => setLoading(false));
  }, [project.id, subProjectId]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleDelete = async (campaign) => {
    if (!(await confirmDelete({title: `Delete campaign "${campaign.name}"?`}))) return;
    CampaignRepository.delete(campaign.id)
      .then(() => {
        toast.success("Campaign deleted");
        setCampaigns(prev => prev.filter(item => item.id !== campaign.id));
      })
      .catch(() => toast.error("Failed to delete campaign"));
  };

  const handleSaved = (saved) => {
    if (!saved) return fetchCampaigns();
    // a campaign moved to another language than the filtered one leaves the list
    const visible = !subProjectId || String(saved.subProjectId) === String(subProjectId);
    setCampaigns(prev => {
      const others = prev.filter(item => item.id !== saved.id);
      if (!visible) return others;
      return prev.some(item => item.id === saved.id) ? prev.map(item => item.id === saved.id ? saved : item) : [saved, ...prev];
    });
  };

  const languageOptions = useMemo(() => [
    {value: "", label: "All sub projects"},
    ...subProjects.map(item => ({value: String(item.id), label: subProjectLabel(item)})),
  ], [subProjects]);

  // Conversations of answered calls open on the registry supervision page
  const conversationHref = useCallback(
    (conversationId) => `${basePath}/supervision?conversation=${conversationId}`, [basePath]);

  if (campaignId) {
    return (
      <ConversationHrefContext.Provider value={conversationHref}>
        <CampaignDetailsNew
          key={campaignId}
          campaignId={campaignId}
          registryProjectId={project.id}
          subProjects={subProjects}
          onBack={() => {
            setSearchParams({});
            fetchCampaigns();
          }}
        />
      </ConversationHrefContext.Provider>
    );
  }

  return (
    <Box sx={{height: "100%", width: "100%", maxWidth: {md: "calc(100% - 128px)"}, mx: "auto", display: "flex", flexDirection: "column", gap: 2, p: {xs: 2, md: 3}, boxSizing: "border-box"}}>
      <CampaignFormDialogNew
        open={openForm}
        onClose={() => setOpenForm(false)}
        campaign={editing}
        projectId={project.id}
        subProjects={subProjects}
        defaultSubProjectId={subProjectId || subProject.id}
        defaultRange={createRange}
        onSaved={handleSaved}
      />

      <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
        <Box>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>OUTBOUND CALLING</Typography>
          <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
            Campaigns
          </Typography>
          <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
            Manage outbound call campaigns, schedules, and call outcomes
          </Typography>
        </Box>
        <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
          <PillSelect value={subProjectId} options={languageOptions} onChange={setSubProjectId}
                      placeholder={"Sub project"} ariaLabel={"Filter by sub project"}/>
          <CollectionViewToggle value={viewMode} onChange={setViewMode} withCalendar withList={false}/>
          <Button
            onClick={() => {
              setEditing(null);
              setCreateRange(null);
              setOpenForm(true);
            }}
            startIcon={<HugeiconsIcon icon={Add01Icon} size={17}/>}
            sx={{
              height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 700,
              color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
            }}
          >
            Create campaign
          </Button>
        </Stack>
      </Stack>

      {viewMode === "calendar" ? (
        <CampaignsCalendar
          campaigns={campaigns}
          loading={loading}
          getExtension={getExtension}
          subProjectOf={subProjects.length > 1 ? languageName : undefined}
          onOpen={(campaign) => setSearchParams({campaign: String(campaign.id)})}
          onEdit={(campaign) => {
            setEditing(campaign);
            setOpenForm(true);
          }}
          onCreate={(startDate, endDate) => {
            setEditing(null);
            setCreateRange({startDate, endDate: endDate || startDate});
            setOpenForm(true);
          }}
        />
      ) : (
        <CampaignsTableNew
          viewMode={viewMode}
          campaigns={campaigns}
          loading={loading}
          getExtension={getExtension}
          subProjectOf={subProjects.length > 1 ? languageName : undefined}
          onOpen={(campaign) => setSearchParams({campaign: String(campaign.id)})}
          onEdit={(campaign) => {
            setEditing(campaign);
            setOpenForm(true);
          }}
          onDelete={handleDelete}
        />
      )}
    </Box>
  );
};
